import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import { formatOrdinalDateTime } from "./clientsTable";

export const MERCHANTS_TABLE_LIMIT = 25;

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export type MerchantRecord = Record<string, unknown> & {
  _id?: string;
  name?: string;
  email?: string;
  status?: string;
  type?: string;
  createdAt?: string;
};

export type MerchantRow = BaseRowItem & {
  raw: MerchantRecord;
  name: string;
  email: string;
  status: string;
  type: string;
  dateCreated: string;
};

export function merchantStatusClass(status: string): string {
  switch (status) {
    case "APPROVED":
      return "text-[#03C668]";
    case "PENDING":
      return "text-[#F28A0F]";
    case "REJECTED":
      return "text-red-500";
    default:
      return "text-[#AEBCC6]";
  }
}

export function buildMerchantRows(merchants: MerchantRecord[]): MerchantRow[] {
  return merchants.map((merchant, index) => ({
    id: str(merchant._id) || `merchant-${index}`,
    raw: merchant,
    name: str(merchant.name) || "—",
    email: str(merchant.email) || "—",
    status: str(merchant.status) || "—",
    type: str(merchant.type) || "—",
    dateCreated: formatOrdinalDateTime(merchant.createdAt),
  }));
}

export function createMerchantColumns(
  onOpen: (row: MerchantRow) => void
): ColumnDef<MerchantRow, unknown>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="capitalize text-[14px] text-white">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "KYC",
      cell: ({ getValue }) => {
        const status = String(getValue());
        return (
          <span
            className={`text-[13px] font-bold ${merchantStatusClass(status)}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#E7EBEE]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "dateCreated",
      header: "Date Created",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onOpen(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function details(m: MerchantRecord): Record<string, unknown> {
  const d = m.details;
  return d && typeof d === "object" ? (d as Record<string, unknown>) : {};
}

export function buildMerchantsCsv(merchants: MerchantRecord[]): string {
  const header = [
    "Name",
    "Email",
    "BN Number",
    "Industry",
    "Address",
    "Size",
    "Country",
    "Description",
    "Status",
    "Type",
    "Verified",
    "Created at",
  ];
  const rows = merchants.map((m) => {
    const d = details(m);
    return [
      csvCell(str(m.name)),
      csvCell(str(m.email)),
      csvCell(str(m.bnNumber)),
      csvCell(str(d.industry) || "Not provided"),
      csvCell(str(d.address) || "Not provided"),
      csvCell(str(d.size) || "Not provided"),
      csvCell(str(d.country) || "Not provided"),
      csvCell(str(d.description) || "No description available"),
      csvCell(str(m.status)),
      csvCell(str(m.type)),
      csvCell(m.isVerified ? "Yes" : "No"),
      csvCell(formatOrdinalDateTime(m.createdAt)),
    ];
  });
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
